import type { NotificationReachItem, OpsAuditRecord } from '@nadoutong/contracts';
import { NOTIFICATION_SEVERITIES } from '@nadoutong/contracts';
import { formatLocalTimestamp } from '../formatters.ts';
import { OpsUserRef } from '../identity/OpsUserRef.tsx';
import { useT } from '../../i18n/index.ts';
import type { I18nKey, TParams } from '../../i18n/index.ts';
import { Chip, Td, Tr } from '../../ui/index.ts';

/**
 * One past send, read out of the audit row's metadata (#1946, #1965 scope).
 *
 * `metadata` arrives as a serialized JSON string written by whatever route
 * logged the action, so a reader is guaranteed nothing about its shape. It is
 * parsed and coerced field by field, and anything missing renders as `—`: a
 * history panel that throws on one malformed row is worse than one that admits
 * it cannot read that row.
 */

/** Whether the batched reach read has answered, and how. */
export type ReachState = 'pending' | 'error' | 'ready';

const MISSING = '—';

export function AnnouncementHistoryRow({
  row,
  reach,
  reachState,
}: {
  readonly row: OpsAuditRecord;
  readonly reach: NotificationReachItem | undefined;
  /** Whether the reach read has answered, and how — never rendered as `0`. */
  readonly reachState: ReachState;
}): JSX.Element {
  const t = useT();
  const sentAt = formatLocalTimestamp(row.created_at, { seconds: true });
  const metadata = parseMetadata(row.metadata);
  const slug = readText(metadata.slug);
  const title = readText(metadata.title);
  const description = readText(metadata.description);
  const severity = NOTIFICATION_SEVERITIES.find((value) => value === metadata.severity);
  const testOrgIds = readStringList(metadata.test_recipients_requested);
  // Used twice — for the scope chip and for suppressing the reach number two
  // cells over — so the two cells cannot drift into disagreeing about what
  // kind of send this row was.
  const isWhitelist = metadata.mode === 'whitelist';
  return (
    <Tr interactive={false} data-testid={`announcements-history-row-${row.id}`}>
      <Td mono title={sentAt}>
        {sentAt}
      </Td>
      <Td mono title={slug}>
        {slug}
      </Td>
      {/* The body text hangs off the title cell. A re-fired slug overwrites
          the notifications rows, so for every send but the latest one this
          audit row is the only surviving copy of what was said — and the
          table has no column wide enough to show it. */}
      <Td emphasis title={description === MISSING ? title : `${title}\n\n${description}`}>
        {title}
      </Td>
      <Td>
        {severity === undefined ? MISSING : t(`announcements.severity.${severity}` as I18nKey)}
      </Td>
      {/* AUDIENCE, AS ITS OWN COLUMN (#1965). A whitelist send and a fleet
          publish differ only in `orgs_targeted` otherwise, and reading intent
          off a count means guessing whether "3" was a draft review or a fleet
          of three. The recipient list is on the tooltip because it is the
          answer to "who actually saw that draft" and the notifications rows
          cannot give it — the fleet publish rewrites them all.

          THE COUNT IN THE CHIP IS THE LIST LENGTH, AND SAYS SO. It comes from
          the recorded `test_recipients_requested` — the list the operator ASKED
          for — not from `orgs_targeted` two cells over, and the two are allowed
          to differ. A bare "Test 3" would be read as "3 orgs received it" — a
          reach claim this cell has no basis for — so the copy names the list
          instead. An unreadable or absent list renders as "not recorded" rather
          than as a list of zero. */}
      <Td {...(testOrgIds.length > 0 ? { title: testOrgIds.join('\n') } : {})}>
        {isWhitelist ? (
          <Chip tone="warn">
            {testOrgIds.length > 0
              ? t('announcements.history.scope.whitelist', { count: String(testOrgIds.length) })
              : t('announcements.history.scope.whitelist.unknown')}
          </Chip>
        ) : metadata.mode === 'fleet' ? (
          // Every send since #1965 stamps a mode. An older row has none, and
          // it predates the whitelist arm entirely, so it can only have been a
          // fleet publish — but it is rendered as unknown rather than asserted
          // as fleet, because "we did not record this" is not a fact about the
          // audience.
          <Chip tone="neutral">{t('announcements.history.scope.fleet')}</Chip>
        ) : (
          MISSING
        )}
      </Td>
      <Td num mono>
        {readCount(metadata.orgs_targeted)}
      </Td>
      <Td num mono>
        {readCount(metadata.created)}
      </Td>
      <Td num mono>
        {readCount(metadata.resurfaced)}
      </Td>
      {/* REACH IS FINGERPRINT-LEVEL, SO A TEST-SEND ROW GETS NO NUMBER. The
          read is keyed on the slug's fingerprint, which every send of that slug
          shares — so a whitelist row and the fleet publish that followed it
          report the SAME current figure. Rendering it here puts "list of 2" and
          "300 / 592" on one line, and the obvious reading of that line is that
          the test send reached 592 orgs: a reach claim about this row that the
          number is not about, which is #1945's exact mechanism.
          There is no per-send reach to fall back to — the fleet publish rewrote
          those rows — so the cell says it has no number for this row rather
          than borrowing one, in the same `n/a` vocabulary the unmeasurable case
          already uses, with its own tooltip for the different cause. It takes
          precedence over the error state deliberately: whether or not the read
          answered, this row has nothing to report. */}
      <Td
        num
        mono
        {...(isWhitelist
          ? { title: t('announcements.history.polled.whitelist.title') }
          : reachState === 'error'
            ? { title: t('announcements.history.polled.unavailable.title') }
            : {})}
      >
        {isWhitelist ? t('announcements.history.polled.unmeasurable') : formatReach(reach, reachState, t)}
      </Td>
      <Td mono title={row.ops_user_id}>
        <OpsUserRef id={row.ops_user_id} />
      </Td>
    </Tr>
  );
}

/**
 * The reachability cell, and the FOUR states it must keep apart.
 *
 * `—` while the read is in flight: a `0 / 0` printed before the answer arrives
 * is a number an operator will act on.
 *
 * `?`, with its own tooltip and a `role="alert"` banner under the table, when
 * the read FAILED. This used to render as `—` too, which meant a platform
 * outage was indistinguishable from a slow load — a column of dashes that an
 * operator reads as "still loading" forever. The quiet-org panel on this same
 * page already treats a failed read as its own state; this is that rule
 * applied to the column that was missing it.
 *
 * `n/a` when `measurable` is false: there is no notification row left to
 * measure against, so the ratio would be `0 / 0`. That is "we cannot tell",
 * not "it reached nobody", and printing `0 / 0` would state the second — the
 * #1945 mistake of asserting something the surface has no way to know. The
 * note under the table says what it means WITHOUT naming a cause: retention is
 * only one of the four ways the denominator gets to zero. A whitelist row
 * renders the same `n/a` from the caller above, for a different reason it
 * carries its own tooltip for.
 *
 * Otherwise `<polled> / <holding>`, which is exactly what was measured and
 * carries its own denominator so nobody has to guess what it is a fraction of.
 */
function formatReach(
  reach: NotificationReachItem | undefined,
  state: ReachState,
  t: (key: I18nKey, params?: TParams) => string,
): string {
  if (state === 'error') return t('announcements.history.polled.unavailable');
  if (state === 'pending' || reach === undefined) return MISSING;
  if (!reach.measurable) return t('announcements.history.polled.unmeasurable');
  return `${reach.orgs_polled_since} / ${reach.orgs_holding}`;
}

function parseMetadata(raw: string): Record<string, unknown> {
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function readText(value: unknown): string {
  return typeof value === 'string' && value.length > 0 ? value : MISSING;
}

function readCount(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : MISSING;
}

function readStringList(value: unknown): readonly string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}
